// Centralized constants and helpers for the hasTravelAdvance field.
// The field is nullable: null = no advance, 'viaje' | 'gnc' | 'viaje_gnc' = advance type.

export type TravelAdvance = "viaje" | "gnc" | "viaje_gnc" | null

export const TRAVEL_ADVANCE_LABELS: Record<string, string> = {
  viaje:     "Viaje",
  gnc:       "GNC",
  viaje_gnc: "Viaje + GNC",
  no:        "No",
}

// Badge colors for each value (null maps to "no")
export const TRAVEL_ADVANCE_COLORS: Record<string, string> = {
  viaje:     "bg-emerald-100 text-emerald-700",
  gnc:       "bg-blue-100 text-blue-700",
  viaje_gnc: "bg-purple-100 text-purple-700",
  no:        "bg-muted text-muted-foreground",
}

// Hover variants for interactive badges
export const TRAVEL_ADVANCE_HOVER: Record<string, string> = {
  viaje:     "hover:bg-emerald-200",
  gnc:       "hover:bg-blue-200",
  viaje_gnc: "hover:bg-purple-200",
  no:        "hover:bg-muted/80",
}

// Cycle order for click-to-toggle: null → viaje → gnc → viaje_gnc → null
const ADVANCE_CYCLE: TravelAdvance[] = [null, "viaje", "gnc", "viaje_gnc"]

// Normalizes any incoming value (including legacy booleans or unknown strings)
// to a valid TravelAdvance member so the cycle always works correctly.
export function normalize(value: unknown): TravelAdvance {
  if (value === true)  return "viaje"   // legacy boolean true → viaje
  if (!value)          return null      // false, null, undefined, "" → null
  if (value === "viaje" || value === "gnc" || value === "viaje_gnc") return value
  return null                           // any other unexpected string
}

export function nextTravelAdvance(current: unknown): TravelAdvance {
  const n = normalize(current)
  const idx = ADVANCE_CYCLE.indexOf(n)
  return ADVANCE_CYCLE[(idx + 1) % ADVANCE_CYCLE.length] ?? null
}

export function travelAdvanceKey(value: unknown): string {
  const n = normalize(value)
  return n ?? "no"
}

export function travelAdvanceLabel(value: unknown): string {
  return TRAVEL_ADVANCE_LABELS[travelAdvanceKey(value)] ?? "No"
}

export function travelAdvanceColor(value: unknown): string {
  return TRAVEL_ADVANCE_COLORS[travelAdvanceKey(value)] ?? "bg-muted text-muted-foreground"
}

export function travelAdvanceHover(value: unknown): string {
  return TRAVEL_ADVANCE_HOVER[travelAdvanceKey(value)] ?? "hover:bg-muted/80"
}

// All options for Select dropdowns
export const TRAVEL_ADVANCE_OPTIONS: { value: TravelAdvance; label: string }[] = [
  { value: null,        label: "No" },
  { value: "viaje",     label: "Viaje" },
  { value: "gnc",       label: "GNC" },
  { value: "viaje_gnc", label: "Viaje + GNC" },
]
